import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { TrendingUp, Zap, Calendar } from 'lucide-react';
import { generationTrend, monthlyGeneration, districts, stateData } from '../mock';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Generation = () => {
  const todayGen = stateData.todayGeneration;
  const weeklyTotal = generationTrend.reduce((sum, day) => sum + day.generation, 0);
  const weeklyAvg = Math.round(weeklyTotal / generationTrend.length);
  const monthlyTotal = monthlyGeneration[monthlyGeneration.length - 1].generation;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Generation & Capacity</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>Energy generation monitoring and analysis</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e3f2fd' }}>
                <Zap className="w-6 h-6" style={{ color: '#3498db' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{todayGen} kWh</div>
            <div className="text-sm" style={{ color: '#576574' }}>Today's Generation</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8f5f1' }}>
                <TrendingUp className="w-6 h-6" style={{ color: '#10ac84' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{weeklyAvg} kWh</div>
            <div className="text-sm" style={{ color: '#576574' }}>Weekly Average</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f3e5f5' }}>
                <Calendar className="w-6 h-6" style={{ color: '#9b59b6' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{monthlyTotal} kWh</div>
            <div className="text-sm" style={{ color: '#576574' }}>This Month (Jul)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fff4e6' }}>
                <Zap className="w-6 h-6" style={{ color: '#f39c12' }} />
              </div>
            </div>
            <div className="text-3xl font-bold mb-2" style={{ color: '#0a3d62' }}>{stateData.totalCapacity} MW</div>
            <div className="text-sm" style={{ color: '#576574' }}>Total Capacity</div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Generation Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" style={{ color: '#3498db' }} />
            7-Day Generation Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <Bar
              data={{
                labels: generationTrend.map(day => 
                  new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                ),
                datasets: [{
                  label: 'Generation (kWh)',
                  data: generationTrend.map(day => day.generation),
                  backgroundColor: 'rgba(52, 152, 219, 0.8)',
                  borderColor: '#3498db',
                  borderWidth: 1,
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: '#576574',
                    }
                  },
                  x: {
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: '#576574',
                    }
                  }
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Monthly Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" style={{ color: '#9b59b6' }} />
            Monthly Generation (Year 2025)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <Line
              data={{
                labels: monthlyGeneration.map(month => month.month),
                datasets: [{
                  label: 'Generation (kWh)',
                  data: monthlyGeneration.map(month => month.generation),
                  borderColor: '#9b59b6',
                  backgroundColor: 'rgba(155, 89, 182, 0.1)',
                  tension: 0.4,
                  fill: true,
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                    callbacks: {
                      label: function(context) {
                        return context.parsed.y.toLocaleString() + ' kWh';
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: '#576574',
                      callback: function(value) {
                        return (value / 1000).toFixed(0) + 'k';
                      }
                    }
                  },
                  x: {
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                      color: '#576574',
                    }
                  }
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* District Generation */}
      <Card>
        <CardHeader>
          <CardTitle>District-wise Today's Generation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {districts.map((district) => {
              const contribution = ((district.todayGen / todayGen) * 100).toFixed(1);
              
              return (
                <div key={district.id}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-4">
                      <span className="font-semibold w-32" style={{ color: '#0a3d62' }}>{district.name}</span>
                      <Badge variant="outline" className="text-xs">{district.sites} sites</Badge>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold" style={{ color: '#3498db' }}>{district.todayGen} kWh</span>
                      <span className="text-sm ml-2" style={{ color: '#576574' }}>({contribution}%)</span>
                    </div>
                  </div>
                  <Progress value={parseFloat(contribution) * 10} className="h-3" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Generation;
